import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  InputNumber,
  Button,
  Table,
  Space,
  Typography,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import type { Employee, Product, PO, WorkOrder } from "./index";
import { apiRequest } from "../../../../../service/apiRequest";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import {
  calculateCFT,
  getWidthQtyTotal,
} from "../../../../../utils/cftCalculator";
import { Wood } from "../../../../../types/Wood";
const { Text } = Typography;

/* ---------------- WIDTH CONFIG ---------------- */

const woodWidths = Array.from({ length: 18 }, (_, i) => i + 3); // 3" to 20"

/* ---------------- TYPES ---------------- */

type IssueItem = {
  key: string;
  woodId: number | null;
  woodTypeId: number | null;
  widths: { [width: number]: number };
  rate: number;
};

interface IssueMasterModalProps {
  visible: boolean;
  isEditMode: boolean;
  initialValues: any;
  onCancel: () => void;
  onCreate: () => Promise<void>;
}
/* ---------------- COMPONENT ---------------- */

const TimberIssueMasterModal: React.FC<IssueMasterModalProps> = ({
  visible,
  isEditMode,
  initialValues,
  onCancel,
  onCreate,
}) => {
  const [form] = Form.useForm();

  const { employees, woods, woodtypes, loaded } = useAppSelector(
    (state) => state.master,
  );
  console.log("employees", employees);
  const [poList, setPoList] = useState<any[]>([]);
  const [poBills, setPoBills] = useState<any[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [employeeList, setEmployeeList] = useState<any[]>([]);
  const [selectType, setSelectType] = useState<"PI" | "Work" | undefined>();

  const [items, setItems] = useState<IssueItem[]>([]);

  /* ---------------- API LOADERS ---------------- */

  const loadPO = async () => {
    const res = await apiRequest({ method: "get", url: "get-performa-invoices" });
    setPoList(res || []);
    return res || [];
  };

  const loadPOBills = async () => {
    try {
      const res = await apiRequest<any[]>({ method: "get", url: "get-po-bill" });
      setPoBills(res || []);
      return res || [];
    } catch {
      setPoBills([]);
      return [];
    }
  };

  const loadWorkOrders = async () => {
    const res = await apiRequest({ method: "get", url: "get-work-orders" });
    setWorkOrders(res || []);
    return res || [];
  };

  const loadDepartments = async () => {
    try {
      const res = await apiRequest<any[]>({ method: "get", url: "departments?status=1" });
      setDepartments(res || []);
      return res || [];
    } catch {
      try {
        const res2 = await apiRequest<any[]>({ method: "get", url: "departments" });
        setDepartments(res2 || []);
        return res2 || [];
      } catch {
        setDepartments([]);
        return [];
      }
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await apiRequest<any[]>({ method: "get", url: "employees?status=1" });
      setEmployeeList(res || []);
      return res || [];
    } catch {
      setEmployeeList([]);
      return [];
    }
  };

  const allEmployees = useMemo(() => {
    return employeeList.length > 0 ? employeeList : (employees || []);
  }, [employeeList, employees]);

  /* ---------------- MACHINERY WORK ORDER FILTER ---------------- */

  const isMachineryWorkOrder = (wo: any) => {
    // 1. Direct machinery department name check on work order
    const deptName = String(
      wo.department_name || wo.wo_department_name || wo.dept_name || ""
    ).toLowerCase();
    if (deptName.includes("machinery") || deptName.includes("machin")) {
      return true;
    }

    // 2. Department ID check
    const machineryDept = departments.find((d: any) =>
      String(d.department_name || d.label || "").toLowerCase().includes("machinery")
    );
    const machineryDeptId = machineryDept ? Number(machineryDept.department_id || machineryDept.id) : 10;

    const woDeptId = Number(wo.department_id || wo.wo_department_id || 0);
    if (woDeptId && (woDeptId === machineryDeptId || woDeptId === 10)) {
      return true;
    }

    // 3. Check assigned employee's department
    const empId = Number(wo.wo_employee_id || wo.employee_id || 0);
    if (empId) {
      const emp = allEmployees.find((e: any) => Number(e.employee_id) === empId);
      if (emp && emp.employee_department) {
        const deptIds = String(emp.employee_department)
          .split(",")
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => !isNaN(id));

        if (deptIds.includes(machineryDeptId) || deptIds.includes(10)) {
          return true;
        }

        const matchesDept = departments.some(
          (d: any) =>
            deptIds.includes(Number(d.department_id || d.id)) &&
            String(d.department_name || d.label || "").toLowerCase().includes("machinery")
        );
        if (matchesDept) {
          return true;
        }
      }
    }

    // 4. Boolean flag check on work order
    if (wo.isMachinery === true) {
      return true;
    }

    // 5. In edit mode, preserve currently saved work order
    if (
      isEditMode &&
      initialValues &&
      (Number(wo.wo_id) === Number(initialValues.im_master_order_no) ||
        wo.wo_number === String(initialValues.im_master_order_no))
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (visible) {
      Promise.all([
        loadPO(),
        loadPOBills(),
        loadWorkOrders(),
        loadDepartments(),
        loadEmployees(),
      ])
        .then(([poRes, billsRes, woRes]) => {
          const loadedWos: WorkOrder[] = woRes || [];
          if (isEditMode && initialValues) {
            const rawWoId = initialValues.im_master_order_no;
            const matchedWo = loadedWos.find(
              (wo) => Number(wo.wo_id) === Number(rawWoId) || wo.wo_number === String(rawWoId)
            );
            const finalWoId = matchedWo ? matchedWo.wo_id : (rawWoId ? Number(rawWoId) : undefined);
            const assignedEmpId = matchedWo
              ? Number(matchedWo.wo_employee_id || matchedWo.employee_id)
              : undefined;
            const finalIssueTo = initialValues.im_issue_to
              ? Number(initialValues.im_issue_to)
              : assignedEmpId;

            form.setFieldsValue({
              issue_to: finalIssueTo,
              issue_type: initialValues.im_issue_type,
              pi_id: initialValues.im_master_pi_no ? Number(initialValues.im_master_pi_no) : undefined,
              work_order_id: finalWoId,
            });
            setSelectType(initialValues.im_issue_type);

            if (Array.isArray((initialValues as any).items)) {
              const groups: { [groupKey: string]: IssueItem } = {};
              (initialValues as any).items.forEach((item: any) => {
                const groupKey = `${item.woodId}-${item.woodTypeId}-${item.rate}`;
                if (!groups[groupKey]) {
                  const emptyWidths = woodWidths.reduce((acc, w) => ({ ...acc, [w]: 0 }), {});
                  groups[groupKey] = {
                    key: groupKey + Math.random(),
                    woodId: item.woodId ? Number(item.woodId) : null,
                    woodTypeId: item.woodTypeId ? Number(item.woodTypeId) : null,
                    rate: Number(item.rate || 0),
                    widths: emptyWidths,
                  };
                }
                if (item.width && groups[groupKey].widths[item.width] !== undefined) {
                  groups[groupKey].widths[item.width] = Number(item.qty || 0);
                }
              });
              setItems(Object.values(groups));
            } else {
              setItems([]);
            }
          } else {
            form.resetFields();
            setSelectType(undefined);
            setItems([]);
          }
        })
        .catch(() => message.error("Failed to load dropdown data"));
    }
  }, [visible, initialValues, isEditMode]);

  /* ---------------- WEIGHTED AVERAGE PRICE LOGIC ---------------- */

  const getAveragePriceForWood = (
    woodId: number | null,
    woodTypeId: number | null,
    requestedCft: number = 0
  ) => {
    if (!woodId && !woodTypeId) return 0;

    const matchingPbg: any[] = [];
    poBills.forEach((bill: any) => {
      const details = bill.po_details || [];
      details.forEach((item: any) => {
        const itemWoodId = Number(item.wood_id || item.pbg_wood_id);
        const itemWoodTypeId = Number(item.wood_type_id || item.pbg_wood_type || item.pbg_wood_type_id);

        const isWoodMatch = !woodId || itemWoodId === Number(woodId);
        const isTypeMatch = !woodTypeId || itemWoodTypeId === Number(woodTypeId);

        if (isWoodMatch && isTypeMatch) {
          const cft = Number(item.pbg_cft || item.pbg_qty || 0);
          const issuedCft = Number(item.pbg_issued_cft || 0);
          const availableCft = Math.max(0, cft - issuedCft);
          const rate = Number(item.pbg_rate || item.pbg_price || 0);
          const billDate = bill.pbill_date || bill.pbill_created_on || '';
          const billId = Number(bill.pbill_id || item.pbg_id || 0);

          if (cft > 0 && rate > 0) {
            matchingPbg.push({
              pbg_id: item.pbg_id,
              cft,
              issued_cft: issuedCft,
              available_cft: availableCft,
              rate,
              billDate,
              billId,
            });
          }
        }
      });
    });

    if (matchingPbg.length === 0) return 0;

    // FIFO Sort: oldest bill first
    matchingPbg.sort((a, b) => {
      if (a.billDate && b.billDate) return new Date(a.billDate).getTime() - new Date(b.billDate).getTime();
      return a.billId - b.billId;
    });

    // Bills with remaining available CFT
    const pendingBills = matchingPbg.filter((b) => b.available_cft > 0);

    if (pendingBills.length > 0) {
      if (requestedCft > 0) {
        let neededCft = requestedCft;
        let totalCost = 0;
        let totalAllocatedCft = 0;

        for (const bill of pendingBills) {
          if (neededCft <= 0) break;
          const take = Math.min(bill.available_cft, neededCft);
          totalCost += take * bill.rate;
          totalAllocatedCft += take;
          neededCft -= take;
        }

        if (totalAllocatedCft > 0) {
          return parseFloat((totalCost / totalAllocatedCft).toFixed(4));
        }
      }

      const totalPendingCft = pendingBills.reduce((sum, b) => sum + b.available_cft, 0);
      const totalPendingAmount = pendingBills.reduce((sum, b) => sum + b.available_cft * b.rate, 0);
      if (totalPendingCft > 0) {
        return parseFloat((totalPendingAmount / totalPendingCft).toFixed(4));
      }
    }

    // Fallback: overall weighted average across all purchase bills
    const totalCft = matchingPbg.reduce((sum, b) => sum + b.cft, 0);
    const totalAmount = matchingPbg.reduce((sum, b) => sum + b.cft * b.rate, 0);
    return totalCft > 0 ? parseFloat((totalAmount / totalCft).toFixed(4)) : 0;
  };

  /* ---------------- TABLE LOGIC ---------------- */

  const addRow = () => {
    const emptyWidths = woodWidths.reduce((acc, w) => ({ ...acc, [w]: 0 }), {});

    setItems((prev) => [
      ...prev,
      {
        key: Date.now().toString(),
        woodId: null,
        woodTypeId: null,
        widths: emptyWidths,
        rate: 0,
      },
    ]);
  };

  const removeRow = (key: string) => {
    setItems((prev) => prev.filter((r) => r.key !== key));
  };

  const updateWood = (key: string, value: number | null) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const avgRate = getAveragePriceForWood(value, row.woodTypeId, calculateRowCFT({ ...row, woodId: value }));
        return {
          ...row,
          woodId: value,
          rate: avgRate > 0 ? avgRate : row.rate,
        };
      }),
    );
  };

  const updateWoodType = (key: string, value: number | null) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const avgRate = getAveragePriceForWood(row.woodId, value, calculateRowCFT({ ...row, woodTypeId: value }));
        return {
          ...row,
          woodTypeId: value,
          rate: avgRate > 0 ? avgRate : row.rate,
        };
      }),
    );
  };

  const updateWidth = (key: string, width: number, value: number | null) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const newWidths = { ...row.widths, [width]: value ?? 0 };
        const tempRow = { ...row, widths: newWidths };
        const newCFT = calculateRowCFT(tempRow);
        const autoRate = getAveragePriceForWood(row.woodId, row.woodTypeId, newCFT);

        return {
          ...row,
          widths: newWidths,
          rate: autoRate > 0 ? autoRate : row.rate,
        };
      }),
    );
  };

  const updateRate = (key: string, value: number | null) => {
    setItems((prev) =>
      prev.map((row) => (row.key === key ? { ...row, rate: value ?? 0 } : row)),
    );
  };

  /* ---------------- CALCULATIONS ---------------- */

  const calculateTotalPcs = (widths: { [width: number]: number }) =>
    Object.values(widths).reduce((acc, val) => acc + val, 0);

  const calculateRowCFT = (row: IssueItem) => {
    const wood = woods.find((w: any) => w.wood_id === row.woodId) as Wood | undefined;
    if (!wood) return 0;
    const widthQtyTotal = getWidthQtyTotal(row.widths);
    return calculateCFT(
      widthQtyTotal,
      Number(wood.wood_length),
      Number(wood.wood_thickness)
    );
  };

  const calculateAmount = (cft: number, rate: number) =>
    parseFloat((cft * rate).toFixed(4));

  const grandTotal = useMemo(() => {
    return items.reduce((sum, row) => {
      const cft = calculateRowCFT(row);
      return sum + calculateAmount(cft, row.rate);
    }, 0);
  }, [items, woods]);

  const hasAtLeastOneWidth = (widths: { [width: number]: number }) =>
    Object.values(widths).some((qty) => qty > 0);

  /* ---------------- TABLE COLUMNS ---------------- */

  const columns = [
    {
      title: (
        <span>
          Wood / Wood Type <span style={{ color: "#ff4d4f" }}>*</span>
        </span>
      ),
      fixed: "left" as const,
      width: 180,
      render: (_: any, record: IssueItem) => (
        <>
          <Select
            showSearch
            allowClear
            placeholder="Select wood *"
            value={record.woodId}
            status={!record.woodId ? "warning" : undefined}
            style={{ width: 200, marginBottom: 5 }}
            onChange={(value) => updateWood(record.key, value)}
            options={woods.map((w) => ({
              label: w.wood_name,
              value: w.wood_id,
            }))}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />

          <Select
            value={record.woodTypeId}
            placeholder="Select Wood Type *"
            showSearch
            status={!record.woodTypeId ? "warning" : undefined}
            optionFilterProp="label"
            style={{ width: 200 }}
            onChange={(value) => updateWoodType(record.key, value)}
            options={woodtypes.map((w) => ({
              label: w.wood_name,
              value: w.wood_id,
            }))}
          />
        </>
      ),
    },

    ...woodWidths.map((width) => ({
      title: `${width}"`,
      width: 70,
      render: (_: any, record: IssueItem) => (
        <InputNumber
          min={0}
          value={record.widths[width]}
          onChange={(val) => updateWidth(record.key, width, val)}
          style={{ width: 65 }}
        />
      ),
    })),


    {
      title: "Total Pcs",
      fixed: "right" as const,
      width: 100,
      render: (_: any, record: IssueItem) => calculateTotalPcs(record.widths),
    },
    {
      title: "CFT",
      fixed: "right" as const,
      width: 100,
      render: (_: any, record: IssueItem) => {
        const cft = calculateRowCFT(record);
        if (!record.woodId) return <span style={{ color: "#aaa" }}>-</span>;
        return <strong>{cft}</strong>;
      },
    },
    {
      title: "Rate",
      fixed: "right" as const,
      width: 100,
      render: (_: any, record: IssueItem) => (
        <InputNumber
          min={0}
          value={record.rate}
          onChange={(val) => updateRate(record.key, val)}
        />
      ),
    },

    {
      title: "Amount",
      fixed: "right" as const,
      width: 120,
      render: (_: any, record: IssueItem) => {
        const cft = calculateRowCFT(record);
        return <strong>{calculateAmount(cft, record.rate)}</strong>;
      },
    },

    {
      title: "",
      width: 60,
      fixed: "right" as const,
      render: (_: any, record: IssueItem) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeRow(record.key)}
        />
      ),
    },
  ];

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async (values: any) => {
    if (!items.length) {
      message.error("Please add at least one item");
      return;
    }

    const invalidWood = items.find((row) => !row.woodId);
    if (invalidWood) {
      message.error("Please select wood for all rows");
      return;
    }

    const invalidWoodType = items.find((row) => !row.woodTypeId);
    if (invalidWoodType) {
      message.error("Please select Wood Type for all rows (Wood Type is compulsory)");
      return;
    }

    const invalidWidths = items.find((row) => !hasAtLeastOneWidth(row.widths));
    if (invalidWidths) {
      message.error("Each row must have at least one width quantity");
      return;
    }

    // 1. Build a pool of all matching PBG items sorted chronologically (FIFO)
    const pbgPool: any[] = [];
    poBills.forEach((bill: any) => {
      const details = bill.po_details || [];
      details.forEach((pbgItem: any) => {
        const pbgId = pbgItem.pbg_id;
        const woodId = Number(pbgItem.wood_id || pbgItem.pbg_wood_id);
        const woodTypeId = Number(pbgItem.wood_type_id || pbgItem.pbg_wood_type || pbgItem.pbg_wood_type_id);
        const totalCft = Number(pbgItem.pbg_cft || pbgItem.pbg_qty || 0);
        const currentIssuedCft = Number(pbgItem.pbg_issued_cft || 0);
        const availableCft = Math.max(0, totalCft - currentIssuedCft);
        const rate = Number(pbgItem.pbg_rate || pbgItem.pbg_price || 0);
        const billDate = bill.pbill_date || bill.pbill_created_on || '';
        const billId = Number(bill.pbill_id || pbgId || 0);

        if (pbgId && totalCft > 0) {
          pbgPool.push({
            pbg_id: pbgId,
            wood_id: woodId,
            wood_type_id: woodTypeId,
            total_cft: totalCft,
            issued_cft: currentIssuedCft,
            available_cft: availableCft,
            rate: rate,
            bill_date: billDate,
            bill_id: billId,
          });
        }
      });
    });

    // FIFO sort: oldest bills first
    pbgPool.sort((a, b) => {
      if (a.bill_date && b.bill_date) return new Date(a.bill_date).getTime() - new Date(b.bill_date).getTime();
      return a.bill_id - b.bill_id;
    });

    // 2. Allocate each issued row against the available PBG pool
    const itemAllocations: Record<string, any[]> = {};
    const allPbgUpdatesMap = new Map<number, { pbg_id: number; pbg_issued_cft: number; added_issued_cft: number; rate: number }>();

    items.forEach((row) => {
      const rowCFT = calculateRowCFT(row);
      let neededCFT = rowCFT;
      const rowAllocs: any[] = [];

      for (const pbg of pbgPool) {
        if (neededCFT <= 0) break;
        const isWoodMatch = !row.woodId || pbg.wood_id === Number(row.woodId);
        const isTypeMatch = !row.woodTypeId || pbg.wood_type_id === Number(row.woodTypeId);

        if (isWoodMatch && isTypeMatch && pbg.available_cft > 0) {
          const takeCft = Math.min(pbg.available_cft, neededCFT);
          const newIssuedTotal = parseFloat((pbg.issued_cft + takeCft).toFixed(3));

          rowAllocs.push({
            pbg_id: pbg.pbg_id,
            issued_cft: parseFloat(takeCft.toFixed(3)),
            rate: pbg.rate,
            new_pbg_issued_cft: newIssuedTotal,
          });

          // Update pool state for sequential allocations
          pbg.available_cft -= takeCft;
          pbg.issued_cft = newIssuedTotal;
          neededCFT -= takeCft;

          // Track update for this pbg_id in crm_purchase_bill_goods
          const existing = allPbgUpdatesMap.get(pbg.pbg_id);
          if (existing) {
            existing.pbg_issued_cft = newIssuedTotal;
            existing.added_issued_cft = parseFloat((existing.added_issued_cft + takeCft).toFixed(3));
          } else {
            allPbgUpdatesMap.set(pbg.pbg_id, {
              pbg_id: pbg.pbg_id,
              pbg_issued_cft: newIssuedTotal,
              added_issued_cft: parseFloat(takeCft.toFixed(3)),
              rate: pbg.rate,
            });
          }
        }
      }

      itemAllocations[row.key] = rowAllocs;
    });

    const pbgUpdatesList = Array.from(allPbgUpdatesMap.values());

    const woId = values.work_order_id;
    const selectedWo = woId
      ? workOrders.find((wo) => Number(wo.wo_id) === Number(woId))
      : null;
    const assignedEmpId = selectedWo
      ? Number(selectedWo.wo_employee_id || selectedWo.employee_id)
      : null;
    const finalIssueTo =
      values.issue_type === "PI" && assignedEmpId
        ? assignedEmpId
        : Number(values.issue_to);

    const payload = {
      ...values,
      issue_to: finalIssueTo,
      items: items.map((row) => ({
        ...row,
        cft: calculateRowCFT(row),
        pbg_allocations: itemAllocations[row.key] || [],
      })),
      pbg_updates: pbgUpdatesList,
      crm_purchase_bill_goods_updates: pbgUpdatesList,
      grand_total: grandTotal,
      issue_master_id: isEditMode && initialValues ? initialValues.im_id : null,
    };

    await apiRequest({
      method: "post",
      url: isEditMode ? "edit-timber-issue-master" : "add-timber-issue-master",
      data: payload,
    });

    message.success(isEditMode ? "Issue updated successfully" : "Issue created successfully");
    form.resetFields();
    setItems([]);
    onCreate();
    onCancel();
  };

  /* ---------------- RENDER ---------------- */

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      width="95%"
      title="Issue Mastesr - Timber"
      footer={null}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {(() => {
          const colSpan = selectType === "PI" ? 6 : 12;
          return (
            <Row gutter={16}>
              {/* 1. FIRST: Issue Type */}
              <Col span={colSpan}>
                <Form.Item
                  name="issue_type"
                  label="Issue Type"
                  rules={[{ required: true, message: "Please select issue type" }]}
                >
                  <Select
                    placeholder="Select Issue Type"
                    showSearch
                    optionFilterProp="label"
                    onChange={(value: "PI" | "Work") => {
                      setSelectType(value);
                      if (value === "Work") {
                        form.setFieldsValue({ pi_id: undefined, work_order_id: undefined, issue_to: undefined });
                      } else {
                        form.setFieldsValue({ pi_id: undefined, work_order_id: undefined, issue_to: undefined });
                      }
                    }}
                  >
                    <Select.Option value="PI" label="PI">PI</Select.Option>
                    <Select.Option value="Work" label="Work">Work</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* 2. IF PI: Show PI Number, then Work Order No, then on select work order show Issue To (read-only) */}
              {selectType === "PI" && (
                <>
                  <Col span={colSpan}>
                    <Form.Item name="pi_id" label="PI Number" rules={[{ required: true, message: "Please select PI Number" }]}>
                      <Select
                        showSearch
                        allowClear
                        placeholder="Search PI"
                        optionFilterProp="label"
                        onChange={() => {
                          form.setFieldsValue({ work_order_id: undefined, issue_to: undefined });
                        }}
                      >
                        {poList.map((po: any) => (
                          <Select.Option key={po.pi_id} value={po.pi_id} label={String(po.pi_order_no)}>
                            {po.pi_order_no}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={colSpan}>
                    <Form.Item noStyle dependencies={["pi_id"]}>
                      {({ getFieldValue }) => {
                        const currentPiId = getFieldValue("pi_id");
                        const machineryWos = workOrders.filter(isMachineryWorkOrder);
                        const filteredWorkOrders = currentPiId
                          ? machineryWos.filter((wo) => Number(wo.wo_pi_id) === Number(currentPiId))
                          : [];

                        return (
                          <Form.Item name="work_order_id" label="Work Order No" rules={[{ required: true, message: "Please select Work Order" }]}>
                            <Select
                              showSearch
                              allowClear
                              disabled={!currentPiId}
                              placeholder={currentPiId ? "Search Work Order No" : "Select PI Number first"}
                              optionFilterProp="label"
                              onChange={(val) => {
                                if (val) {
                                  const selectedWo = workOrders.find((wo) => Number(wo.wo_id) === Number(val));
                                  const assignedEmpId = selectedWo
                                    ? Number(selectedWo.wo_employee_id || selectedWo.employee_id)
                                    : undefined;
                                  if (assignedEmpId) {
                                    form.setFieldsValue({ issue_to: assignedEmpId });
                                  } else {
                                    form.setFieldsValue({ issue_to: undefined });
                                  }
                                } else {
                                  form.setFieldsValue({ issue_to: undefined });
                                }
                              }}
                            >
                              {filteredWorkOrders.map((wo) => (
                                <Select.Option
                                  key={wo.wo_id}
                                  value={wo.wo_id}
                                  label={wo.wo_number || `WO-${wo.wo_id}`}
                                >
                                  {wo.wo_number || `WO-${wo.wo_id}`}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>

                  {/* 3. On select work order, next option show issue to (read only) */}
                  <Form.Item noStyle dependencies={["work_order_id"]}>
                    {({ getFieldValue }) => {
                      const currentWoId = getFieldValue("work_order_id");
                      if (!currentWoId) return null;

                      const selectedWo = workOrders.find((wo) => Number(wo.wo_id) === Number(currentWoId));
                      const assignedEmpId = selectedWo
                        ? Number(selectedWo.wo_employee_id || selectedWo.employee_id)
                        : null;

                      let employeeOptions = allEmployees.map((emp) => ({
                        label: `${emp.employee_fname || ""} ${emp.employee_lname || ""}`.trim() || `Employee #${emp.employee_id}`,
                        value: Number(emp.employee_id),
                      }));

                      if (assignedEmpId) {
                        const matchedEmp = allEmployees.find(
                          (emp) => Number(emp.employee_id) === assignedEmpId
                        );
                        if (matchedEmp) {
                          employeeOptions = [
                            {
                              label: `${matchedEmp.employee_fname || ""} ${matchedEmp.employee_lname || ""}`.trim() || `Employee #${matchedEmp.employee_id}`,
                              value: Number(matchedEmp.employee_id),
                            },
                          ];
                        } else if (selectedWo?.employee_fname) {
                          employeeOptions = [
                            {
                              label: `${selectedWo.employee_fname} ${selectedWo.employee_lname || ""}`.trim(),
                              value: assignedEmpId,
                            },
                          ];
                        }
                      }

                      return (
                        <Col span={colSpan}>
                          <Form.Item
                            name="issue_to"
                            label="Issue To"
                            rules={[{ required: true, message: "Please select an employee" }]}
                            tooltip="Assigned employee from selected Work Order (Read-only)"
                          >
                            <Select
                              placeholder="Assigned employee"
                              showSearch
                              disabled={!!assignedEmpId}
                              optionFilterProp="label"
                              options={employeeOptions}
                            />
                          </Form.Item>
                        </Col>
                      );
                    }}
                  </Form.Item>
                </>
              )}

              {/* 4. IF WORK: Show Issue To as next option (editable dropdown) */}
              {selectType === "Work" && (
                <Col span={colSpan}>
                  <Form.Item
                    name="issue_to"
                    label="Issue To"
                    rules={[{ required: true, message: "Please select an employee" }]}
                  >
                    <Select
                      placeholder="Select employee"
                      showSearch
                      optionFilterProp="label"
                      options={allEmployees.map((emp) => ({
                        label: `${emp.employee_fname || ""} ${emp.employee_lname || ""}`.trim() || `Employee #${emp.employee_id}`,
                        value: Number(emp.employee_id),
                      }))}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          );
        })()}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addRow}
          style={{ marginBottom: 12 }}
        >
          Add Wood
        </Button>

        <Table
          columns={columns}
          dataSource={items}
          pagination={false}
          rowKey="key"
          scroll={{ x: "max-content" }}
        />

        <Row justify="end" style={{ marginTop: 16, flexDirection: "column", alignItems: "flex-end" }}>
          <Text strong style={{ fontSize: 16 }}>
            Grand Total : {grandTotal.toFixed(4)}
          </Text>
        </Row>

        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Submit Issue Maser - Timber
            </Button>
          </Space>
        </Row>
      </Form>
    </Modal>
  );
};

export default TimberIssueMasterModal;
